
















import { computed, defineComponent, onMounted, onUnmounted, ref } from "@vue/composition-api";
import { Vue } from "vue/types/vue";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { InputMessageSearch } from "@scrile/api-provider/dist/api/MessagesProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { StreamUserJoinData } from "@/types";
import { EVENTS } from "@/const";
import BaseIconButton from "@/components/BaseIconButton";
import CustomScroll from "@/components/CustomScroll";
import InfiniteScroll from "@/components/InfiniteScroll";
import StreamMessage from "../StreamMessage";

export default defineComponent({
  name: "StreamChat",
  components: {
    InfiniteScroll,
    StreamMessage,
    CustomScroll,
    BaseIconButton,
  },
  props: {
    threadId: { type: String, required: true },
    token: { type: String, required: true },
    streamStartedDate: { type: String, required: true },
  },
  setup(props) {
    const { items, data, addItem, enableScroll, getFirstPage, getNextPage } = usePager<Message, InputMessageSearch>(
      page =>
        providers.MessagesProvider.find({
          data: {
            threadId: props.threadId,
            page,
            sort: "TIME,DESC",
          },
        }),
      result => {
        const index = result.findIndex(i => i.time < props.streamStartedDate);
        if (index > -1) {
          result.splice(index, result.length - index);
          data.value && (data.value.hasNextPage = false);
        }
      }
    );

    const messageListNode = ref<Vue | undefined>();

    const messageList = computed(() => Array.from(items.value).reverse());

    const checkIsAdmin = (user: LiveChatUser) => {
      return checkRole(user?.user?.role ?? "", "admin");
    };

    const { authUser } = useAuthUser();
    const { shouldScrollToBottom, scrollNode } = useKeepCustomScrollPosition(messageListNode, {
      keepScroll: true,
      behavior: "smooth",
    });

    let unsubscribe = () => {};

    const onNewMessageReceived = (message: Message) => {
      addItem(message, "start");
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (message.senderId === authUser.value?.id || bottomScrollTopPosition - scrollNode.value.scrollTop <= 100) {
        shouldScrollToBottom.value = true;
      }
    };

    const createJoinMessage = (data?: StreamUserJoinData) => {
      if (!data || !data.user || checkIsAdmin(data.user) || !data.threadId || data.threadId !== props.threadId) return;
      const joinMessageId = Date.now() + "";
      const joinMessage: Message = {
        id: joinMessageId,
        senderId: "0",
        senderUser: data.user.user,
        text: $t("{0} joined", [data.user.user ? data.user.user.screenName : $t("Guest user #{0}", [data.user.id])]),
        details: { type: "join" },
        threadId: "",
        time: new Date().toISOString(),
        link: null,
        attachment: [],
        likes: {
          subject: {
            subjectType: LikeSubjectType.MESSAGE,
            subjectId: joinMessageId,
          },
          count: 0,
          liked: false,
        },
      };
      onNewMessageReceived(joinMessage);
    };
    const subscribeLiveChatNewMessages = async () => {
      const subscription = await providers.LivechatsProvider.subscribeMessageReceived(
        props.token,
        onNewMessageReceived
      );
      unsubscribe = () => subscription.unsubscribe();
    };

    getFirstPage();

    onMounted(() => {
      subscribeLiveChatNewMessages();
      emitter.on<StreamUserJoinData>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
    });

    onUnmounted(() => {
      emitter.off<StreamUserJoinData>(EVENTS.STREAM_USER_JOIN, createJoinMessage);
      return unsubscribe();
    });

    return { messageList, messageListNode, enableScroll, getNextPage };
  },
});
